import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Users } from "lucide-react";
import AppLayout from "../../components/AppLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import api, { formatCurrency } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { useTerms } from "../../lib/BrandContext";
import { showPlanLimitToast } from "../../lib/upgradeCta";
import { invalidateNearLimitCache } from "../../components/NearLimitTip";
import { notifyInstallMilestone } from "../../lib/installMilestone";
import { toast } from "sonner";

// Normalize age-group display so "12U" and "U12" look the same on screen.
// Stored value isn't modified; only the rendered label is consistent.
const normalizeAgeGroup = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim().toUpperCase();
  // "12U" → "U12" (matches the rest of the app)
  const m = s.match(/^(\d{1,2})U$/);
  if (m) return `U${m[1]}`;
  // Bare "9" / "10" / etc → "U9" / "U10" (seed-data safety net)
  if (/^\d{1,2}$/.test(s)) return `U${s}`;
  return s;
};

export default function Teams() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { terms } = useTerms();
  const [teams, setTeams] = useState(null);
  const [usage, setUsage] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", sport: "", age_group: "", season: "" });
  // Default = show active only. Archived teams live behind a toggle so
  // an old / completed group can be dropped from the working view without
  // ever losing its records. The toggle mirrors the roster's "Show
  // archived" checkbox on the team-detail page for consistency.
  const [showArchived, setShowArchived] = useState(false);

  const load = () => {
    // GET /teams accepts ?archived=<true|false>. We always pass the flag
    // explicitly so a coach toggling the checkbox gets a clean list
    // (server-side filter) rather than the client filtering after the
    // fact. Falls back to setting an empty array so the "no teams yet"
    // empty state paints instead of the loading spinner.
    api.get(`/teams?archived=${showArchived}`).then((r) => setTeams(r.data)).catch(() => setTeams([]));
    // Plan usage drives the "at limit" CTA state below. Failure is non-blocking.
    api.get("/orgs/mine/plan").then((r) => setUsage(r.data)).catch(() => setUsage(null));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);

  const onCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/teams", form);
      setForm({ name: "", sport: "", age_group: "", season: "" });
      setOpen(false);
      toast.success(`${terms.Team} created successfully`, {
        description: `Next: Add your first ${terms.athlete}.`,
      });
      notifyInstallMilestone();
      invalidateNearLimitCache();
      load();
    } catch (err) {
      // Close the dialog first so the Upgrade Plan toast action is fully clickable
      setOpen(false);
      invalidateNearLimitCache();
      showPlanLimitToast(err, navigate, { fallback: `Could not create ${terms.team}` });
    }
  };

  // Compute "at team limit" state from plan usage. Limit may be null for
  // unlimited / enterprise plans — only treat as at-limit when both numbers exist.
  const activeTeams = usage?.usage?.active_teams ?? null;
  const teamLimit = usage?.limits?.teams ?? null;
  const atTeamLimit = activeTeams != null && teamLimit != null && activeTeams >= teamLimit;

  return (
    <AppLayout>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-ink-900 sm:text-4xl">
            {showArchived ? `Archived ${terms.teams}` : `Your ${terms.teams}`}
          </h1>
          <p className="mt-1 text-base text-ink-600">
            {showArchived
              ? `Restore or permanently delete ${terms.teams} you archived earlier.`
              : `Tap a ${terms.team} to see ${terms.athletes}, fees, and payments.`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-ink-600">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-4 w-4 rounded border-ink-300 text-brand focus:ring-brand"
              data-testid="teams-show-archived"
            />
            Show archived
          </label>
          {atTeamLimit ? (
          <Button
            disabled
            className="h-12 cursor-not-allowed bg-ink-200 font-semibold text-ink-500 hover:bg-ink-200"
            data-testid="teams-create-button"
            title={usage?.early_access ? `Early Access limit reached for ${terms.teams}.` : `You've reached your ${terms.team} limit. Upgrade to add another ${terms.team}.`}
          >
            <Plus className="mr-1 h-4 w-4" /> Add {terms.Team}
          </Button>
        ) : (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="h-12 bg-brand font-semibold text-white hover:bg-brand-hover" data-testid="teams-create-button">
              <Plus className="mr-1 h-4 w-4" /> Add {terms.Team}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create a {terms.team}</DialogTitle></DialogHeader>
            <form onSubmit={onCreate} className="space-y-4" data-testid="teams-create-form">
              <div>
                <Label>{terms.Team} name</Label>
                <Input
                  required
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Westside U12 Lightning"
                  className="h-12"
                  data-testid="team-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Sport</Label>
                  <Input
                    required
                    value={form.sport}
                    onChange={(e) => setForm((p) => ({ ...p, sport: e.target.value }))}
                    placeholder="Soccer"
                    className="h-12"
                    data-testid="team-sport-input"
                  />
                </div>
                <div>
                  <Label>Age group</Label>
                  <Input
                    required
                    value={form.age_group}
                    onChange={(e) => setForm((p) => ({ ...p, age_group: e.target.value }))}
                    placeholder="U12"
                    className="h-12"
                    data-testid="team-age-input"
                  />
                </div>
              </div>
              <div>
                <Label>Season</Label>
                <Input
                  required
                  value={form.season}
                  onChange={(e) => setForm((p) => ({ ...p, season: e.target.value }))}
                  placeholder="Spring 2026"
                  className="h-12"
                  data-testid="team-season-input"
                />
              </div>
              <DialogFooter>
                <Button type="submit" className="h-12 bg-brand font-semibold text-white hover:bg-brand-hover" data-testid="team-create-submit">Create {terms.Team}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        )}
        </div>
      </div>

      {/* Simple at-limit banner — appears only when the coach has filled their plan's team slots. */}
      {atTeamLimit ? (
        <div
          className="mt-4 flex flex-col items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/60 p-4 sm:flex-row sm:items-center sm:justify-between"
          data-testid="teams-at-limit-banner"
        >
          <div>
            <p className="font-semibold text-ink-900">
              You&apos;ve reached your {terms.team} limit.
            </p>
            <p className="text-sm text-ink-700">
              {usage?.early_access
                ? `That's the Early Access limit. Archive a ${terms.team} to free up a spot, or contact support@squadfee.com if you need more.`
                : `Upgrade to add another ${terms.team}.`}
            </p>
          </div>
          {usage?.early_access ? (
            <a href="mailto:support@squadfee.com?subject=Squad%20Fee%20Early%20Access%20limit" data-testid="teams-at-limit-contact">
              <Button variant="outline" className="h-11 font-semibold">Contact support</Button>
            </a>
          ) : (
          <Link to="/coach/settings#plan" data-testid="teams-at-limit-upgrade">
            <Button className="h-11 bg-brand font-semibold text-white hover:bg-brand-hover">
              Upgrade plan
            </Button>
          </Link>
          )}
        </div>
      ) : null}

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {teams === null ? (
          <div className="col-span-full flex h-32 items-center justify-center text-ink-500" data-testid="teams-loading">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand border-t-transparent" />
          </div>
        ) : (
        <>
        {teams.map((t) => {
          const isArchived = t.status !== "active";
          const owed = t.unpaid_amount || 0;
          return (
          <div key={t.id} className="flex flex-col rounded-2xl border border-ink-100 bg-white p-5 shadow-sm card-hover" data-testid={`team-card-${t.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-heading text-lg font-bold text-ink-900 break-words">{t.name}</h3>
                <p className="mt-0.5 text-sm text-ink-500">{t.sport} · {normalizeAgeGroup(t.age_group)}</p>
              </div>
              {isArchived ? (
                <span className="inline-flex shrink-0 items-center rounded-full bg-ink-100 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-ink-600">
                  Archived
                </span>
              ) : null}
            </div>

            {/* Two simple facts: roster size + still owed */}
            <div className="mt-4 flex flex-1 flex-col gap-2">
              <div className="flex items-center justify-between border-t border-ink-100 pt-3">
                <span className="text-sm text-ink-600">{terms.Athletes}</span>
                <span className="font-heading text-base font-bold text-ink-900" data-testid={`team-players-${t.id}`}>{t.player_count}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-ink-600">Still owed</span>
                <span
                  className={`font-heading text-base font-bold ${owed > 0 ? "text-rose-700" : "text-emerald-700"}`}
                  data-testid={`team-owed-${t.id}`}
                >
                  {formatCurrency(owed)}
                </span>
              </div>
            </div>

            {/* Single primary action — archive moves inside the team detail's More menu */}
            <div className="mt-4">
              <Link to={`/coach/teams/${t.id}`} data-testid={`team-open-${t.id}`}>
                <Button className="h-11 w-full bg-brand font-semibold text-white hover:bg-brand-hover">View {terms.Team}</Button>
              </Link>
            </div>
          </div>
          );
        })}
        {teams !== null && teams.length === 0 ? (
          <div className="col-span-full rounded-xl border-2 border-dashed border-ink-200 bg-white p-10 text-center" data-testid="teams-empty">
            <Users className="mx-auto h-8 w-8 text-ink-300" />
            {showArchived ? (
              <>
                <p className="mt-3 font-semibold text-ink-900">No archived {terms.teams}</p>
                <p className="text-sm text-ink-500">Archive an active {terms.team} from its {terms.team} page to see it here.</p>
              </>
            ) : (
              <>
                <p className="mt-3 font-semibold text-ink-900">You haven&apos;t created a {terms.team} yet.</p>
                <p className="text-sm text-ink-500">Create your first {terms.team} to add {terms.athletes} and fees.</p>
                {!atTeamLimit ? (
                  <Button
                    onClick={() => setOpen(true)}
                    className="mt-4 h-11 bg-brand px-5 font-semibold text-white hover:bg-brand-hover"
                    data-testid="teams-empty-cta"
                  >
                    <Plus className="mr-1 h-4 w-4" /> Add {terms.Team}
                  </Button>
                ) : null}
              </>
            )}
          </div>
        ) : null}
        </>
        )}
      </div>
    </AppLayout>
  );
}
